import { drawRooftop } from "./rooftopRenderer";
export type CityPhase = "day" | "dusk" | "night";
type Point = { x: number; y: number };
export const projectPlots = [
  { u: 6.4, v: 6, w: 1.65, d: 1.4, h: 140, name: "EXPEDITION 33", accent: "#86e4ee" },
  { u: 10.3, v: 3, w: 1.3, d: 1.25, h: 111, name: "NIER : AUTOMATA", accent: "#adcee9" },
  { u: 2.7, v: 5.9, w: 1.55, d: 1.25, h: 56, name: "PAGOMOA", accent: "#ffbd9c" },
  { u: 3, v: 10.2, w: 1.5, d: 1.45, h: 73, name: "MOONLIGHTER", accent: "#d0b2ed" },
  { u: 7.1, v: 10.3, w: 1.6, d: 1.4, h: 65, name: "METAL SLUG 3", accent: "#efbb89" },
  { u: 10.1, v: 7.3, w: 1.6, d: 1.4, h: 67, name: "SLIMEDICER", accent: "#9ce0cb" },
];
export const cityPoint = (u: number, v: number): Point => ({ x: 240 + (u - v) * 18, y: 125 + (u + v) * 9 });
export const projectBounds = (p: typeof projectPlots[number]) => {
  const a = cityPoint(p.u, p.v);
  return { x: a.x - p.w * 18 - 3, y: a.y - p.h - (p.w + p.d) * 9 - 20, width: (p.w + p.d) * 18 + 6, height: p.h + (p.w + p.d) * 9 + 32 };
};
export function trafficPosition(index: number, time: number) {
  // Interior streets only: the horizon and perimeter roads carry no moving sprites.
  const road = [4, 8, 12][index % 3];
  const t = 1 + (((time * (index % 2 ? -.38 : .38) + index * 2.73) % 14 + 14) % 14);
  return index % 2 ? { u: road + .13, v: t } : { u: t, v: road - .13 };
}
const noise = (a: number, b = 0) => { const n = Math.sin(a * 127.1 + b * 311.7) * 43758.5453; return n - Math.floor(n); };
const palettes = {
  day: { sky: ["#548fae", "#9cc6ce", "#d1dbcc"], far: ["#8eafba","#628da3","#426c88"], ground:"#416078", tile:"#4b6d81", road:"#233d55", line:"#71959f", left:"#456b88", right:"#284965", roof:"#6b91a1", dark:"#1a344e", cool:"#a6d7de", warm:"#f3cbaa", water:"#315976" },
  dusk: { sky: ["#83769e", "#dc96ab", "#ffcaab"], far: ["#c785a0","#a46889","#715977"], ground:"#3c405c", tile:"#4c4d68", road:"#282c42", line:"#8b7c8b", left:"#5a5379", right:"#353851", roof:"#86738c", dark:"#222b43", cool:"#91cbd0", warm:"#ffc3a0", water:"#3c405e" },
  night: { sky: ["#0b2346", "#174b7b", "#33779b"], far: ["#286a91","#225579","#193f65"], ground:"#162e48", tile:"#203e57", road:"#0c1e34", line:"#416b81", left:"#234b70", right:"#112e4f", roof:"#396385", dark:"#0b1e35", cool:"#8dd7e7", warm:"#ffbc98", water:"#102d4b" },
};
export function drawCity(ctx: CanvasRenderingContext2D, phase: CityPhase, selected: number, hour: number, time: number) {
  const p = palettes[phase], lit = phase !== "day";
  const rect = (x: number,y: number,w: number,h: number,c: string) => { ctx.fillStyle=c;ctx.fillRect(Math.round(x),Math.round(y),Math.max(1,Math.round(w)),Math.max(1,Math.round(h))); };
  const poly = (points: Point[],color: string) => {ctx.fillStyle=color;ctx.beginPath();points.forEach((pt,i)=>i ? ctx.lineTo(pt.x,pt.y) : ctx.moveTo(pt.x,pt.y));ctx.closePath();ctx.fill();};
  const line = (points: Point[],color: string,width=1) => {ctx.strokeStyle=color;ctx.lineWidth=width;ctx.beginPath();points.forEach((pt,i)=>i ? ctx.lineTo(pt.x,pt.y) : ctx.moveTo(pt.x,pt.y));ctx.stroke();};
  const blend = (a: string,b: string,t: number) => "#" + [1,3,5].map(i=>Math.round(parseInt(a.slice(i,i+2),16)*(1-t)+parseInt(b.slice(i,i+2),16)*t).toString(16).padStart(2,"0")).join("");
  for(let y=0;y<300;y+=2) { const t=Math.min(y/185,1);rect(0,y,480,2,t<.5?blend(p.sky[0],p.sky[1],t*2):blend(p.sky[1],p.sky[2],t*2-1)); }
  if(phase==="night") for(let i=0;i<95;i++) {ctx.globalAlpha=.35+noise(i,3)*.6;rect(noise(i,1)*480,noise(i,2)*140,1,i%19===0?2:1,"#d0eafa");}
  ctx.globalAlpha=1;
  const progress=Math.max(0,Math.min(1,(hour-6)/13)), sunX=50+progress*360, sunY=112-Math.sin(progress*Math.PI)*77;
  const celestial = (x:number,y:number,r:number,color:string) => {for(let dy=-r;dy<=r;dy++){const dx=Math.floor(Math.sqrt(r*r-dy*dy));rect(x-dx,y+dy,dx*2,1,color);}};
  celestial(phase==="night"?78:sunX,phase==="night"?48:sunY,phase==="dusk"?29:phase==="night"?10:15,phase==="night"?"#d7e8db":"#fff2d2");
  if(phase==="night"){rect(74,44,3,3,"#b4cbd1");rect(81,50,2,4,"#b4cbd1");}
  // Broken horizontal cloud bands retain the low-resolution pixel texture.
  for(let i=0;i<28;i++){const x=(noise(i,8)*540+time*.12)%540-50,y=18+noise(i,9)*97;ctx.globalAlpha=phase==="night"?.07:.13;rect(x,y,25+noise(i,7)*130,1+i%3,phase==="dusk"?"#ffe6be":"#d1e4e5");}
  ctx.globalAlpha=1;
  for(let layer=0;layer<3;layer++){
    const baseline=153+layer*18;
    for(let i=0;i<48;i++){
      const x=i*11-9+noise(i,layer)*5, w=6+noise(i+50,layer)*16, h=10+noise(i,layer+4)*(28+layer*15);
      rect(x,baseline-h,w,h,p.far[layer]);
      if(i%4===0){rect(x+w*.45,baseline-h-6,1,6,p.far[layer]);rect(x+w*.45,baseline-h-7,1,1,p.warm);}
      for(let wy=baseline-h+4;wy<baseline;wy+=4)for(let wx=x+2;wx<x+w-1;wx+=3)if(noise(wx,wy)>.5){ctx.globalAlpha=.25+noise(wx+1,wy)*.5;rect(wx,wy,1,2,noise(wx,wy+2)>.86?p.warm:p.cool);}
      ctx.globalAlpha=1;
    }
  }
  // The town continues off the frame; the diamond grid defines its streets.
  rect(0,158,480,142,p.ground);
  poly([cityPoint(0,0),cityPoint(16,0),cityPoint(16,16),cityPoint(0,16)],p.ground);
  for(let u=0;u<16;u++)for(let v=0;v<16;v++){
    if((u+v)%2===0)poly([cityPoint(u,v),cityPoint(u+1,v),cityPoint(u+1,v+1),cityPoint(u,v+1)],blend(p.ground,p.tile,.35));
    line([cityPoint(u,v),cityPoint(u+1,v),cityPoint(u+1,v+1)],p.tile,.4);
  }
  rect(0,222,42,78,p.water);
  for(let i=0;i<65;i++){ctx.globalAlpha=.1+noise(i)*.2;rect(noise(i,4)*85,218+noise(i,5)*82,3+noise(i,6)*18,1,p.cool);}
  ctx.globalAlpha=1;
  const streets=[0,4,8,12];
  streets.forEach(s=>{
    line([cityPoint(s,0),cityPoint(s,16)],p.tile,13);
    line([cityPoint(0,s),cityPoint(16,s)],p.tile,13);
    line([cityPoint(s,0),cityPoint(s,16)],p.road,9);
    line([cityPoint(0,s),cityPoint(16,s)],p.road,9);
    for(let t=0;t<16;t+=.65){
      line([cityPoint(s,t),cityPoint(s,t+.22)],p.line,.55);
      line([cityPoint(t,s),cityPoint(t+.22,s)],p.line,.55);
    }
  });
  const box=(u:number,v:number,w:number,d:number,h:number,z=0,seed=0,accent?:string)=>{
    const a=cityPoint(u,v),b=cityPoint(u-w,v),c=cityPoint(u-w,v-d),e=cityPoint(u,v-d);
    [a,b,c,e].forEach(pt=>pt.y-=z);
    const up=(pt:Point)=>({x:pt.x,y:pt.y-h});
    poly([a,b,up(b),up(a)],p.left);poly([a,e,up(e),up(a)],p.right);poly([up(a),up(b),up(c),up(e)],p.roof);
    line([up(b),up(c),up(e)],blend(p.roof,p.cool,.25),.8);
    line([a,up(a)],p.dark,1);
    // Independent lit/dark windows on both faces, aligned with their perspective.
    for(let side=0;side<2;side++){
      const extent=side?d:w, dir=side?1:-1;
      for(let col=.18;col<extent-.08;col+=.23)for(let y=5;y<h-3;y+=4){
        const on=noise(col*61+seed,y+side*39)>(lit?.34:.76);
        const color=on?(noise(col*19+seed,y)>.7?p.warm:p.cool):p.dark;
        rect(a.x+dir*col*18,a.y-col*9-h+y,side?1:2,2,on?color:blend(color,side?p.right:p.left,.4));
      }
      for(let level=11;level<h;level+=14)line([{x:a.x,y:a.y-h+level},{x:a.x+dir*extent*18,y:a.y-extent*9-h+level}],p.dark,.6);
    }
    if(accent)line([up(b),up(a),a,e,up(e)],accent,.7);
    return {a,b,c,e};
  };
  type Item={depth:number;draw:()=>void};
  const items:Item[]=[];
  // Deterministic blocks keep the city stable while cars and lights move.
  for(let u=-3;u<16;u++)for(let v=-3;v<16;v++){
    if((u<0||v<0)&&u+v<7)continue;
    if(((u%4)+4)%4===0||((v%4)+4)%4===0)continue;
    if(projectPlots.some(b=>Math.abs(u-b.u)<1.9&&Math.abs(v-b.v)<1.9))continue;
    const seed=u*29+v*103,h=12+noise(u,v)*30, w=.65+noise(u+5,v)*.2, d=.65+noise(u,v+6)*.2;
    items.push({depth:u+v,draw:()=>{
      const a=cityPoint(u+.3,v+.3);if(a.x < -40||a.x>520||a.y-h>310)return;
      box(u+.3,v+.3,w,d,h,0,seed);
      if(noise(u+2,v)>.45){box(u+.14,v+.1,.25,.22,4,h,seed);rect(a.x-3,a.y-h-12,1,8,p.dark);}
      if(noise(u,v+9)>.7){line([{x:a.x-7,y:a.y-h-4},{x:a.x-7,y:a.y-h-14}],p.dark);rect(a.x-11,a.y-h-12,9,1,p.dark);}
    }});
  }
  projectPlots.forEach((b,index)=>{
    items.push({depth:b.u+b.v,draw:()=>{
      const a=cityPoint(b.u,b.v),accent=b.accent;
      if(index===0){
        box(b.u,b.v,b.w,b.d,101,0,120,accent);
        box(b.u-.2,b.v-.2,b.w-.4,b.d-.4,24,101,124,accent);
        box(b.u-.42,b.v-.4,.62,.58,15,125,126,accent);
        const top=cityPoint(b.u-.66,b.v-.65);
        line([{x:top.x,y:top.y-140},{x:top.x,y:top.y-163}],p.cool,2);
        rect(top.x,top.y-164,2,2,p.warm);
        line([{x:a.x-6,y:a.y-8},{x:a.x-6,y:a.y-97}],b.accent,1);
        line([{x:a.x+7,y:a.y-18},{x:a.x+7,y:a.y-98}],p.cool,1);
        // Buttresses and the suspended service bridge echo the reference tower.
        box(b.u-.05,b.v+1,.35,.4,62,0,100);
        box(b.u+1,b.v-.05,.4,.35,72,0,101);
        const l=cityPoint(b.u-.05,b.v+.8),r=cityPoint(b.u+.8,b.v-.05);
        line([{x:l.x,y:l.y-54},{x:r.x,y:r.y-64}],p.dark,5);
        line([{x:l.x,y:l.y-56},{x:r.x,y:r.y-66}],p.cool,1);
      }else if(index===1){
        box(b.u,b.v,b.w,b.d,88,0,220,accent);
        box(b.u-.15,b.v-.15,.4,.85,23,88,224,accent);
        box(b.u-.76,b.v-.14,.36,.85,17,88,225);
        for(let y=18;y<84;y+=5)line([{x:a.x+7,y:a.y-y-4},{x:a.x+17,y:a.y-y-9}],p.cool,.6);
      }else{
        box(b.u,b.v,b.w,b.d,b.h-8,0,index*117,accent);
        box(b.u-.25,b.v-.2,b.w-.5,b.d-.4,8,b.h-8,index*119);
        if(index===2){
          for(let level=8;level<b.h-10;level+=12)line([{x:a.x-b.w*18,y:a.y-level-b.w*9},{x:a.x,y:a.y-level},{x:a.x+b.d*18,y:a.y-level-b.d*9}],p.warm,1);
          box(b.u-.3,b.v-.2,.32,.35,6,b.h,330);
        }else if(index===3){
          const roof=cityPoint(b.u-.6,b.v-.6);
          celestial(roof.x,roof.y-b.h-4,6,p.cool);
          rect(roof.x-8,roof.y-b.h,16,3,p.roof);
          line([{x:roof.x+13,y:roof.y-b.h},{x:roof.x+13,y:roof.y-b.h-16}],p.dark);
          line([{x:a.x-12,y:a.y-18},{x:a.x-12,y:a.y-38}],b.accent,3);
        }else{
          const roof=cityPoint(b.u-.6,b.v-.5);
          rect(roof.x-10,roof.y-b.h-11,20,10,p.dark);
          rect(roof.x-8,roof.y-b.h-9,16,6,b.accent);
          rect(roof.x-5,roof.y-b.h-8,2,2,p.dark);rect(roof.x+3,roof.y-b.h-8,2,2,p.dark);
        }
      }
      {
        const corners=[cityPoint(b.u+.15,b.v+.15),cityPoint(b.u-b.w-.15,b.v+.15),cityPoint(b.u-b.w-.15,b.v-b.d-.15),cityPoint(b.u+.15,b.v-b.d-.15)];
        line([...corners,corners[0]],b.accent,selected===index?2:1);
        line([{x:a.x-3,y:a.y-8},{x:a.x-3,y:a.y-b.h+8}],b.accent,2);
      }
    }});
  });
  // Traffic, pedestrians and lamps share the buildings' depth ordering.
  for(let i=0;i<18;i++){
    const {u,v}=trafficPosition(i,time),a=cityPoint(u,v),dir=i%2?-1:1;
    items.push({depth:u+v,draw:()=>{
      line([{x:a.x-4,y:a.y-dir*2},{x:a.x+4,y:a.y+dir*2}],p.dark,5);
      line([{x:a.x-4,y:a.y-2-dir*2},{x:a.x+4,y:a.y-2+dir*2}],i%3===0?p.warm:"#7998b0",3);
      rect(a.x,a.y-3,2,2,p.cool);rect(a.x+dir*4,a.y+dir-1,2,1,"#fff2cd");
      if(lit){ctx.globalAlpha=.2;poly([{x:a.x+dir*5,y:a.y+dir*2},{x:a.x+dir*15,y:a.y+dir*3},{x:a.x+dir*13,y:a.y+dir*8}],p.warm);ctx.globalAlpha=1;}
    }});
  }
  for(let i=0;i<16;i++){
    const u=4.4+(i%2)*4,v=1+((time*.1+i*.83)%11),a=cityPoint(u,v);
    items.push({depth:u+v,draw:()=>{rect(a.x,a.y-4,2,3,i%2?p.warm:p.cool);rect(a.x,a.y-6,2,2,"#deb7a2");rect(a.x,a.y,1,2,p.dark);}});
  }
  for(let u=0;u<=12;u+=4)for(let v=1;v<=12;v+=2){
    const a=cityPoint(u+.35,v);
    items.push({depth:u+v+.35,draw:()=>{
      if(lit){ctx.globalAlpha=.12;poly([{x:a.x-10,y:a.y},{x:a.x,y:a.y-5},{x:a.x+10,y:a.y},{x:a.x,y:a.y+5}],p.warm);ctx.globalAlpha=1;}
      rect(a.x,a.y-13,1,13,p.dark);rect(a.x,a.y-14,5,1,p.dark);rect(a.x+3,a.y-13,3,1,lit?"#ffe1ad":p.cool);
    }});
  }
  items.sort((a,b)=>a.depth-b.depth).forEach(item=>item.draw());
  drawRooftop(ctx, phase, time);
  ctx.globalAlpha=1;
}
